import React, { useState } from 'react';

// --- Icons (Simulated Lucide React for a clean look) ---
const PlusCircleIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v8"/><path d="M8 12h8"/></svg>;
const SettingsIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.39a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73v.18a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.1a2 2 0 0 1-1-1.72v-.51a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>;
const XIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
const EditIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>;
const TrashIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>;
const ArrowRightIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>;

// --- Constants ---
const PRIMARY_COLOR = 'indigo-600';
const PRIMARY_HOVER = 'indigo-700';
const PRIMARY_BG_LIGHT = 'indigo-50';

// Initial Pipeline Stages
const initialStages = [
    { id: 1, name: 'Application', type: 'initial', color: 'bg-blue-500' },
    { id: 2, name: 'Screening Call', type: 'interview', color: 'bg-yellow-500' },
    { id: 3, name: 'Technical Interview', type: 'interview', color: 'bg-orange-500' },
    { id: 4, name: 'Final Interview', type: 'interview', color: 'bg-red-500' },
    { id: 5, name: 'Offer Extended', type: 'success', color: 'bg-green-500' },
];

let nextId = initialStages.length + 1;

// Stage Colors for Selection
const stageColors = [
    'bg-blue-500', 'bg-yellow-500', 'bg-orange-500', 'bg-red-500', 
    'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-teal-500'
];

/**
 * Stage Management Modal Component
 */
const StageModal = ({ isOpen, onClose, stageData, onSave }) => {
    const [name, setName] = useState(stageData?.name || '');
    const [color, setColor] = useState(stageData?.color || stageColors[0]);
    const isEdit = !!stageData?.id;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (name.trim()) {
            onSave({
                ...stageData,
                name: name.trim(),
                color: color,
                // Type is usually fixed for standard stages, but we default to 'interview' for new custom ones
                type: stageData?.type || 'interview',
            });
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        // Modal Overlay
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-75 transition-opacity duration-300">
            {/* Modal Content - Fixed maximum width and centered */}
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden relative transform scale-100 transition-transform duration-300">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                    <h2 className="text-xl font-poppins text-gray-800 font-normal">
                        {isEdit ? 'Edit Pipeline Stage' : 'Add New Pipeline Stage'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition"
                        aria-label="Close"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(80vh-70px)]">
                    
                    {/* Stage Name Input */}
                    <div>
                        <label htmlFor="stageName" className="block text-sm font-medium text-gray-700 font-roboto mb-1">
                            Stage Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            id="stageName"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-300 focus:border-indigo-500 transition duration-150 shadow-sm text-sm font-roboto"
                            placeholder="e.g., Hiring Manager Review"
                        />
                    </div>

                    {/* Color Picker */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 font-roboto mb-2">
                            Stage Color
                        </label>
                        <div className="flex flex-wrap gap-3">
                            {stageColors.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setColor(c)}
                                    className={`w-8 h-8 rounded-full border-2 transition-all duration-150 ${c}
                                        ${color === c ? 'ring-4 ring-offset-2 ring-indigo-500' : 'hover:scale-105 border-gray-300'}
                                    `}
                                    aria-label={`Select color ${c.replace('bg-', '')}`}
                                >
                                    {color === c && <XIcon className="w-4 h-4 text-white mx-auto transform rotate-45" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Preview */}
                    <div className="pt-4 border-t border-gray-100">
                        <p className="text-sm font-medium text-gray-700 font-roboto mb-2">Stage Preview:</p>
                        <div className={`p-3 rounded-lg flex items-center justify-between text-white font-poppins font-semibold shadow-md ${color}`}>
                            {name || 'New Stage Name'}
                            <span className="text-xs opacity-80">Interview</span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-roboto font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-150"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 text-sm font-roboto font-medium rounded-lg text-white shadow-md transition duration-150
                                bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} disabled:bg-gray-400
                            `}
                            disabled={!name.trim()}
                        >
                            {isEdit ? 'Save Changes' : 'Add Stage'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


/**
 * Main Hiring Pipeline Component
 */
const HiringPipelineSetup = () => {
    const [stages, setStages] = useState(initialStages);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentStage, setCurrentStage] = useState(null); // Stage data being edited

    const openAddModal = () => {
        setCurrentStage(null); // Clear data for new addition
        setIsModalOpen(true);
    };

    const openEditModal = (stage) => {
        setCurrentStage(stage);
        setIsModalOpen(true);
    };

    const handleSaveStage = (stage) => {
        if (stage.id) {
            // Edit existing stage
            setStages(stages.map(s => s.id === stage.id ? stage : s));
        } else {
            // Add new stage
            const newStage = { ...stage, id: nextId++ };
            // Add new stage before 'Offer Extended' if it exists, otherwise add at the end
            const offerIndex = stages.findIndex(s => s.type === 'success');
            if (offerIndex !== -1) {
                const newStages = [...stages];
                newStages.splice(offerIndex, 0, newStage);
                setStages(newStages);
            } else {
                setStages([...stages, newStage]);
            }
        }
    };

    const handleDeleteStage = (id) => {
        if (window.confirm('Are you sure you want to delete this pipeline stage?')) {
            setStages(stages.filter(s => s.id !== id));
        }
    };
    
    // Sort stages: initial -> interview types (custom/default) -> success
    const sortedStages = [...stages].sort((a, b) => {
        const order = ['initial', 'interview', 'success'];
        return order.indexOf(a.type) - order.indexOf(b.type);
    });

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-6 sm:p-8 font-roboto">
            {/* Global Font Styles */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@400;500;700&display=swap');
                    .font-poppins {
                        font-family: 'Poppins', sans-serif;
                    }
                    .font-roboto {
                        font-family: 'Roboto', sans-serif;
                    }
                    .bg-indigo-600 { background-color: #4f46e5; }
                    .hover\\:bg-indigo-700:hover { background-color: #4338ca; }
                    .text-indigo-600 { color: #4f46e5; }
                    .ring-indigo-500 { --tw-ring-color: #6366f1; }
                    .ring-indigo-300 { --tw-ring-color: #a5b4fc; }
                `}
            </style>

            <div className="max-w-4xl w-full bg-white rounded-xl shadow-2xl border border-gray-100 mt-4 overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 bg-white">
                    <h1 className="text-2xl font-poppins font-normal text-gray-800 flex items-center">
                        <SettingsIcon className="w-6 h-6 mr-2 text-indigo-600" />
                        Hiring Pipeline Setup
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 font-roboto">
                        Define the sequence of stages candidates move through for a typical job opening.
                    </p>
                </div>

                {/* Main Content Area */}
                <div className="p-5 md:p-6 space-y-6">
                    
                    {/* Stage List Header and Add Button */}
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                        <h2 className="text-lg font-poppins font-medium text-gray-700">
                            Current Stages ({stages.length})
                        </h2>
                        <button
                            onClick={openAddModal}
                            className={`px-4 py-2 text-sm font-roboto font-medium rounded-lg text-white shadow-md flex items-center space-x-1 transition duration-150
                                bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER}
                            `}
                        >
                            <PlusCircleIcon className="w-4 h-4" />
                            <span>Add New Stage</span>
                        </button>
                    </div>

                    {/* Pipeline Visualization (List) */}
                    <div className="space-y-4">
                        {sortedStages.map((stage, index) => (
                            <div key={stage.id} className="flex items-center">
                                {/* Stage Card */}
                                <div className={`flex-1 p-3 rounded-l-lg border-l-4 shadow-sm border ${stage.color.replace('bg-', 'border-')}`}>
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center space-x-3">
                                            <span className={`text-lg font-poppins font-semibold ${stage.color.replace('bg-', 'text-')}`}>
                                                {index + 1}.
                                            </span>
                                            <span className="text-base font-roboto font-medium text-gray-800">
                                                {stage.name}
                                            </span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${stage.type === 'success' ? 'bg-green-100 text-green-700' : stage.type === 'initial' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'} font-medium`}>
                                                {stage.type.charAt(0).toUpperCase() + stage.type.slice(1)}
                                            </span>
                                        </div>
                                        
                                        {/* Actions (Only allow editing/deleting non-default stages for simplicity) */}
                                        {(stage.type !== 'initial' && stage.type !== 'success') && (
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => openEditModal(stage)}
                                                    className="p-1 text-gray-500 hover:text-indigo-600 rounded-full hover:bg-indigo-50 transition"
                                                    aria-label={`Edit ${stage.name}`}
                                                >
                                                    <EditIcon />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteStage(stage.id)}
                                                    className="p-1 text-gray-500 hover:text-red-600 rounded-full hover:bg-red-50 transition"
                                                    aria-label={`Delete ${stage.name}`}
                                                >
                                                    <TrashIcon />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Arrow Separator (unless it's the last one) */}
                                {index < sortedStages.length - 1 && (
                                    <div className="w-10 flex justify-center text-gray-400">
                                        <ArrowRightIcon className="w-4 h-4"/>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            {/* Stage Configuration Modal */}
            <StageModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                stageData={currentStage}
                onSave={handleSaveStage}
            />
        </div>
    );
};

export default HiringPipelineSetup;